import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Drawer, Tabs, Spin, message, Button, Space } from 'antd';
import type { TabsProps } from 'antd';
import {
  InfoCircleOutlined,
  ApiOutlined,
  CodeOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import type { NodeSingular } from 'cytoscape';
import { OPMApi } from '../../../../api/opmApi';
import type { ProcessFuncCodeDetailDto } from '../../../../api/opmApi';
import BasicInfoTab from './BasicInfoTab';
import FunctionDefTab from './FunctionDefTab';
import FunctionExecTab from './FunctionExecTab';
import type { ProcessDefinitionDrawerProps, SchemaField } from './types';
import { parseBackendSchema, fieldsToInputSchema, fieldsToOutputSchema } from './types';

const ProcessDefinitionDrawer: React.FC<ProcessDefinitionDrawerProps> = ({
  visible,
  node,
  onClose,
  ontologyId,
}) => {
  const [activeTab, setActiveTab] = useState<string>('basic');
  const [loading, setLoading] = useState(false);
  const [definition, setDefinition] = useState<ProcessFuncCodeDetailDto | null>(null);
  const [inputFields, setInputFields] = useState<SchemaField[]>([]);
  const [outputFields, setOutputFields] = useState<SchemaField[]>([]);
  const [schemaDirty, setSchemaDirty] = useState(false);

  const processNodeId = useMemo(() => {
    if (!node) return '';
    const mmRef = node.data('MMRef');
    return mmRef?.id?.toString() || '';
  }, [node]);

  const nodeName = useMemo(() => {
    if (!node) return 'Process';
    return node.data('displayLabel') || node.data('label') || 'Process';
  }, [node]);

  const loadDefinition = useCallback(async () => {
    if (!processNodeId || !ontologyId) return;
    setLoading(true);
    try {
      const def = await OPMApi.getProcessDefinition(ontologyId, processNodeId);
      setDefinition(def);

      setInputFields(parseBackendSchema(def.inputSchema));
      setOutputFields(parseBackendSchema(def.outputSchema));
      setSchemaDirty(false);
    } catch (error: any) {
      console.error('加载Process定义失败:', error);
      setDefinition(null);
      setInputFields([]);
      setOutputFields([]);
    } finally {
      setLoading(false);
    }
  }, [processNodeId, ontologyId]);

  useEffect(() => {
    if (visible && processNodeId) {
      loadDefinition();
      setActiveTab('basic');
    }
  }, [visible, processNodeId, loadDefinition]);

  const handleInputFieldsChange = (fields: SchemaField[]) => {
    setInputFields(fields);
    setSchemaDirty(true);
  };

  const handleOutputFieldsChange = (fields: SchemaField[]) => {
    setOutputFields(fields);
    setSchemaDirty(true);
  };

  const handleSaveSchema = async () => {
    if (!definition || !processNodeId) return;

    const newInputSchema = fieldsToInputSchema(inputFields);
    const newOutputSchema = fieldsToOutputSchema(outputFields);

    try {
      await OPMApi.saveProcessInfo(ontologyId, processNodeId, {
        language: definition.language,
        inputSchema: newInputSchema,
        outputSchema: newOutputSchema,
      });

      // 保存成功后重新加载数据
      await loadDefinition();
      setSchemaDirty(false);
      message.success('约束参数保存成功');
    } catch (error: any) {
      message.error(error.message || '保存失败');
    }
  };

  const handleDefinitionUpdate = (def: ProcessFuncCodeDetailDto) => {
    setDefinition(def);
    setInputFields(parseBackendSchema(def.inputSchema));
    setOutputFields(parseBackendSchema(def.outputSchema));
    setSchemaDirty(false);
  };

  const inputSchema = useMemo(() => {
    return fieldsToInputSchema(inputFields);
  }, [inputFields]);

  const outputSchema = useMemo(() => {
    return fieldsToOutputSchema(outputFields);
  }, [outputFields]);

  const tabItems: TabsProps['items'] = [
    {
      key: 'basic',
      label: (
        <span>
          <InfoCircleOutlined /> 基本信息
        </span>
      ),
      children: (
        <BasicInfoTab
          definition={definition}
          ontologyId={ontologyId}
          processNodeId={processNodeId}
          onDefinitionUpdate={handleDefinitionUpdate}
        />
      ),
    },
    {
      key: 'funcdef',
      label: (
        <span>
          <ApiOutlined /> 函数定义
          {schemaDirty && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>●</span>}
        </span>
      ),
      children: (
        <div>
          <FunctionDefTab
            definition={definition}
            inputFields={inputFields}
            outputFields={outputFields}
            onInputFieldsChange={handleInputFieldsChange}
            onOutputFieldsChange={handleOutputFieldsChange}
          />
          {schemaDirty && (
            <div style={{ textAlign: 'right', padding: '8px 0' }}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveSchema}
              >
                保存约束
              </Button>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'exec',
      label: (
        <span>
          <CodeOutlined /> 函数执行
        </span>
      ),
      children: (
        <FunctionExecTab
          definition={definition}
          ontologyId={ontologyId}
          processNodeId={processNodeId}
          inputSchema={inputSchema}
          outputSchema={outputSchema}
          onDefinitionUpdate={handleDefinitionUpdate}
        />
      ),
    },
  ];

  return (
    <Drawer
      title={`Process 定义 - ${nodeName}`}
      placement="right"
      width={960}
      open={visible}
      onClose={onClose}
      destroyOnClose
      styles={{
        body: { padding: '0 24px 24px' },
      }}
      maskClosable={false}
      extra={
        <Space>
          {definition?.status && (
            <span
              style={{
                fontSize: 12,
                color:
                  definition.status === 'VALID'
                    ? '#52c41a'
                    : definition.status === 'INVALID'
                    ? '#ff4d4f'
                    : '#999',
              }}
            >
              {definition.status}
            </span>
          )}
        </Space>
      }
    >
      <div
        onKeyDown={(e) => {
          const isCopyPaste =
            (e.ctrlKey || e.metaKey) && ['c', 'v', 'x'].includes(e.key);
          const isUndo = (e.ctrlKey || e.metaKey) && e.key === 'z';
          const isRedo = (e.ctrlKey || e.metaKey) && e.key === 'y';
          const isSelectAll = (e.ctrlKey || e.metaKey) && e.key === 'a';
          const isSave = (e.ctrlKey || e.metaKey) && e.key === 's';

          if (isCopyPaste || isUndo || isRedo || isSelectAll || isSave) {
            e.stopPropagation();
          }

          if (isSave) {
            e.preventDefault();
          }
        }}
      >
        <Spin spinning={loading}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            style={{ marginTop: -8 }}
          />
        </Spin>
      </div>
    </Drawer>
  );
};


export default ProcessDefinitionDrawer;
